// CONTROLS screen (Settings → Controller → Button mapping, or the title CONTROLS button): every action with its controller
// button and keyboard key, rebinding by "press a button…", conflict swap, reset to defaults. Logic lives in bindings.ts.
import {
  ACTION, ACTIONS, ActionId, BindCtx, BindingProfile, cellText, CTX_LABEL, defaultProfile, Device, KVLike, keyLabel, loadBindings,
  PadFamilyName, padLabel, PadOptions, rebind, resolve, storeBindings,
} from './bindings';
import type { Input, UiAction } from './input';

export interface ControlsHost {
  input: Input;
  kv: KVLike | null;
  family(): PadFamilyName;
  click(): void;
  deny(): void;
  close(): void;
  /** bindings or options changed: refresh on-screen prompts and the Settings rows */
  changed(): void;
}

const $ = <T extends HTMLElement>(s: string) => document.querySelector(s) as T;
const CAPTURE_MS = 6000;
const ORDER: BindCtx[] = ['run', 'any', 'menu', 'draft'];
type Row = ActionId | '@reset' | '@back';

export class ControlsMenu {
  profile: BindingProfile;
  sel = 0;
  col: Device = 'pad';
  private capturing: { action: ActionId; dev: Device } | null = null;
  private timer = 0;
  private readonly rows: Row[] = [...ORDER.flatMap((c) => ACTIONS.filter((a) => a.ctx === c).map((a) => a.id)), '@reset', '@back'];

  constructor(private host: ControlsHost) {
    this.profile = loadBindings(host.kv);
    host.input.setBindings(resolve(this.profile));
    $('#ctl-list').addEventListener('click', (e) => {
      const t = e.target as HTMLElement, row = t.closest('[data-r]') as HTMLElement | null; if (!row || this.capturing) return;
      this.sel = Number(row.dataset.r);
      const cell = t.closest('[data-c]') as HTMLElement | null; if (cell) this.col = cell.dataset.c as Device;
      this.activate();
    });
  }

  /** first bound input of an action, as a label (HUD prompts / descriptions); '' if none */
  first(action: ActionId, dev: Device): string {
    if (dev === 'pad') { const b = this.profile.pad[action][0]; return b === undefined ? '' : padLabel(b, this.host.family()); }
    const k = this.profile.keys[action][0]; return k ? keyLabel(k) : ACTION[action].keyExtra ?? '';
  }
  setOpt<K extends keyof PadOptions>(k: K, v: PadOptions[K]): void { this.profile.opts[k] = v; this.save(); }

  open(): void { this.sel = 0; this.col = 'pad'; this.render(); }
  close(): void { this.cancel(false); this.host.close(); }

  private msgTimer = 0;
  /** inline feedback line (the HUD toast layer is hidden on menu screens) */
  private msg(text: string, cls = '', dur = 2): void {
    const el = $('#ctl-msg'); el.textContent = text; el.className = cls;
    clearTimeout(this.msgTimer); this.msgTimer = window.setTimeout(() => { el.textContent = ''; }, dur * 1000);
  }
  private save(): void { storeBindings(this.host.kv, this.profile); this.host.input.setBindings(resolve(this.profile)); this.host.changed(); }

  private activate(): void {
    const r = this.rows[this.sel];
    if (r === '@back') { this.close(); return; }
    if (r === '@reset') {
      this.profile = defaultProfile(); this.save(); this.host.click();
      this.msg('CONTROLS RESET TO DEFAULTS', '', 1.6); this.render(); return;
    }
    const def = ACTION[r];
    if (this.col === 'pad' ? def.padFixed : def.keyFixed) {
      this.host.deny(); this.msg(r.startsWith('move') ? 'MOVE IS THE STICK — SWAP STICKS IN SETTINGS' : 'THIS ONE IS FIXED', 'warn', 1.6); return;
    }
    this.capturing = { action: r, dev: this.col };
    this.host.input.capture = { dev: this.col, cb: (code) => this.finish(code) };
    clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.cancel(true), CAPTURE_MS);
    this.host.click(); this.render();
  }
  private cancel(say: boolean): void {
    clearTimeout(this.timer);
    if (!this.capturing) return;
    this.capturing = null; this.host.input.capture = null;
    if (say) this.msg('NO CHANGE', '', 1);
    this.render();
  }
  private finish(code: number | string | null): void {
    const c = this.capturing; clearTimeout(this.timer); this.capturing = null;
    if (!c) return;
    if (code === null) { this.msg('NO CHANGE', '', 1); this.render(); return; }
    const res = rebind(this.profile, c.action, c.dev, code);
    if (!res.ok) {
      this.host.deny();
      this.msg(res.reason === 'required' ? 'CONFIRM AND BACK NEED A BUTTON EACH' : res.reason === 'reserved' ? (c.dev === 'pad' ? 'THE D-PAD IS KEPT FOR MENU NAVIGATION' : 'THAT KEY IS KEPT FOR MENUS (ESC, ENTER, ARROWS)') : 'THIS ONE IS FIXED', 'warn', 2);
    } else if (res.changed) {
      this.save(); this.host.click();
      if (res.swapped) this.msg(`SWAPPED WITH ${ACTION[res.swapped].label.toUpperCase()}`, '', 1.8);
      else if (res.unbound) this.msg(`${ACTION[res.unbound].label.toUpperCase()} IS NOW UNBOUND`, 'warn', 2.4);
    }
    this.render();
  }

  handle(a: UiAction): void {
    if (this.capturing) return; // the capture owns the next press
    const n = this.rows.length;
    if (a === 'back' || a === 'pause') { this.close(); return; }
    if (a === 'down') this.sel = (this.sel + 1) % n;
    else if (a === 'up') this.sel = (this.sel + n - 1) % n;
    else if (a === 'left' || a === 'right') this.col = this.col === 'pad' ? 'key' : 'pad';
    else if (a === 'confirm') { this.activate(); return; }
    else return;
    this.host.click(); this.render();
  }

  render(): void {
    const fam = this.host.family();
    let html = '', last: BindCtx | '' = '';
    this.rows.forEach((r, i) => {
      if (r === '@reset' || r === '@back') {
        html += `<div class="crow btnrow ${r.slice(1)}${i === this.sel ? ' focus' : ''}" data-r="${i}"><span>${r === '@reset' ? 'RESET TO DEFAULTS' : 'BACK'}</span></div>`;
        return;
      }
      const d = ACTION[r];
      if (d.ctx !== last) { html += `<div class="cgroup">${CTX_LABEL[d.ctx]}</div>`; last = d.ctx; }
      const cell = (dev: Device) => {
        const fixed = dev === 'pad' ? d.padFixed : d.keyFixed;
        const cap = this.capturing && this.capturing.action === r && this.capturing.dev === dev;
        const txt = cap ? (dev === 'pad' ? `Press a button… (${padLabel(9, fam)} cancels)` : 'Press a key… (Esc cancels)') : cellText(this.profile, r, dev, fam);
        const foc = i === this.sel && this.col === dev;
        return `<span class="cc ${dev}${fixed ? ' fixed' : ''}${foc ? ' focus' : ''}${cap ? ' cap' : ''}" data-c="${dev}">${txt}</span>`;
      };
      html += `<div class="crow${i === this.sel ? ' focus' : ''}" data-r="${i}"><span class="cl">${d.label}</span>${cell('pad')}${cell('key')}</div>`;
    });
    $('#ctl-list').innerHTML = html;
    $('#ctl-list .crow.focus')?.scrollIntoView({ block: 'nearest' });
    const r = this.rows[this.sel];
    $('#ctl-desc').textContent = this.capturing ? `Press the new ${this.capturing.dev === 'pad' ? 'controller button' : 'key'} for ${ACTION[this.capturing.action].label}. Waits ${CAPTURE_MS / 1000} s.`
      : r === '@reset' ? 'Put every button, key and stick option back to the defaults.'
      : r === '@back' ? 'Leave the Controls screen.'
      : ACTION[r].ctx === 'run' && r.startsWith('move') ? 'Keyboard movement. The controller moves with the stick (swap sticks in Settings).'
      : `Pick a column and press ${padLabel(this.profile.pad.confirm[0] ?? 0, fam)} / Enter, then the new input. A clash swaps the two.`;
  }
}
